import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Globe, Clock, ChatCircleDots, ShieldCheck, Link as LinkIcon, DeviceMobile,
  ArrowSquareOut, NotePencil, Prohibit, CurrencyInr,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * #3 — Customer Details right-side panel. Opens as a 3rd column (right slide-over)
 * when the admin/handler clicks the profile/dashboard from an embedded chat on
 * desktop. Listens for `dws:open-customer-details` (detail: { customerId,
 * roomId, actorId }). Auto-closes on `dws:close-customer-details` and on any
 * route change, so switching chats / panels removes it.
 */
const INACTIVE_DAYS = 30;

const CustomerDetailsPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ctx, setCtx] = useState(null); // { customerId, roomId, actorId }
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({});
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (customerId, roomId) => {
    setLoading(true);
    try {
      const [p, n] = await Promise.allSettled([
        axios.get(`${API}/customers/profile/${customerId}`, { params: roomId ? { room_id: roomId } : {} }),
        axios.get(`${API}/customers/${customerId}/notes`),
      ]);
      if (p.status === 'fulfilled') {
        const r = p.value.data || {};
        setData(r.customer || r || null);
        setStats({
          total_messages: r.total_messages,
          total_deposit: r.total_deposit,
          total_withdraw: r.total_withdraw,
          context_chat_type: r.context_chat_type,
        });
      }
      if (n.status === 'fulfilled') setNotes(n.value.data?.notes || []);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail || {};
      if (!d.customerId) return;
      setCtx(d);
      setData(null);
      setStats({});
      load(d.customerId, d.roomId);
    };
    const onClose = () => setCtx(null);
    window.addEventListener('dws:open-customer-details', onOpen);
    window.addEventListener('dws:close-customer-details', onClose);
    return () => {
      window.removeEventListener('dws:open-customer-details', onOpen);
      window.removeEventListener('dws:close-customer-details', onClose);
    };
  }, [load]);

  // Auto-close whenever the route changes (moved to another chat / panel / page).
  useEffect(() => { setCtx(null); }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setCtx(null); };
    if (ctx) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [ctx]);

  if (!ctx) return null;

  const name = data?.personal_name || data?.username || 'Customer';
  const ip = data?.ip_address || data?.registration_ip || '—';
  const created = data?.created_at ? new Date(data.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
  const totalMsgs = (stats.total_messages ?? '—');
  const blocked = data?.is_blocked || data?.blocked;
  const source = data?.is_inquiry ? 'Inquiry Link' : (data?.created_by_admin ? 'Admin Created' : (data?.created_by_handler ? 'Handler Created' : 'Direct'));
  const loc = data?.ip_history?.[0] ? [data.ip_history[0].city, data.ip_history[0].state, data.ip_history[0].country].filter(Boolean).join(', ') : (data?.last_login_city ? [data.last_login_city, data.last_login_state].filter(Boolean).join(', ') : 'Web');

  // Active / Inactive — inactive when no activity for > 30 days.
  const lastActiveRaw = data?.last_seen || data?.last_login_at || data?.created_at;
  const daysSince = lastActiveRaw ? (Date.now() - new Date(lastActiveRaw).getTime()) / 86400000 : Infinity;
  const isInactive = daysSince > INACTIVE_DAYS;
  const statusLabel = blocked ? 'Blocked' : (isInactive ? 'Inactive' : 'Active');
  const statusColor = blocked ? 'text-red-600' : (isInactive ? 'text-gray-500' : 'text-emerald-600');
  const dotColor = blocked ? 'bg-red-500' : (isInactive ? 'bg-gray-400' : 'bg-emerald-500');

  // Total deposit / withdraw for the panel's chat context.
  const ct = stats.context_chat_type;
  const fmtInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const totalRow = ct === 'deposit'
    ? { label: 'Total Deposit', value: fmtInr(stats.total_deposit), tone: 'text-emerald-700' }
    : ct === 'withdrawal'
      ? { label: 'Total Withdraw', value: fmtInr(stats.total_withdraw), tone: 'text-blue-700' }
      : null;

  const Row = ({ icon: Icon, label, children }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="flex items-center gap-2 text-[13px] text-slate-500"><Icon size={16} /> {label}</span>
      <span className="text-[13px] font-medium text-slate-800 text-right truncate max-w-[55%]">{children}</span>
    </div>
  );

  const act = async (fn) => { try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  const addNote = () => {
    const note = window.prompt('Add a note for this customer:');
    if (!note || !note.trim()) return;
    act(async () => {
      const res = await axios.post(`${API}/customers/${ctx.customerId}/notes`, { note: note.trim(), actor_id: ctx.actorId });
      setNotes((prev) => [res.data.note, ...prev]);
      toast.success('Note added');
    });
  };
  const blockUser = () => {
    if (!window.confirm(`Block ${name}? They will lose access immediately.`)) return;
    act(async () => {
      await axios.post(`${API}/clients/block`, { customer_id: ctx.customerId, blocked_by: ctx.actorId, reason: 'Blocked from Customer Details' });
      toast.success('Customer blocked');
      setData((d) => (d ? { ...d, is_blocked: true, blocked: true } : d));
    });
  };

  const QA = ({ icon: Icon, label, onClick, tone }) => (
    <button onClick={onClick} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-semibold border ${tone}`} data-testid={`cdp-qa-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon size={16} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="w-[340px] flex-shrink-0 h-full bg-white border-l border-gray-200 flex flex-col overflow-y-auto shadow-xl" data-testid="customer-details-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b sticky top-0 bg-white z-10">
        <span className="flex items-center gap-2 font-bold text-slate-800"><ShieldCheck size={18} weight="bold" className="text-indigo-600" /> Customer Details</span>
        <button onClick={() => setCtx(null)} className="p-1.5 hover:bg-gray-100 rounded-full text-slate-500" data-testid="cdp-close-btn"><X size={18} weight="bold" /></button>
      </div>

      {loading && !data ? (
        <div className="p-6 text-center text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 text-white flex items-center justify-center font-bold text-lg">{name[0]?.toUpperCase()}</div>
            <div className="min-w-0">
              <div className="font-bold text-slate-800 truncate">{name}</div>
              <div className={`text-xs font-semibold flex items-center gap-1.5 ${statusColor}`}>
                <span className={`w-2 h-2 rounded-full ${dotColor}`} /> {statusLabel}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-2 divide-y divide-gray-50">
            <Row icon={Globe} label="IP Address">{ip}</Row>
            <Row icon={Clock} label="Created">{created}</Row>
            <Row icon={ChatCircleDots} label="Total Messages">{totalMsgs}</Row>
            <Row icon={ShieldCheck} label="Status">
              <span className={`inline-flex items-center gap-1.5 ${statusColor}`}>
                <span className={`w-2 h-2 rounded-full ${dotColor}`} /> {statusLabel}
              </span>
            </Row>
            <Row icon={LinkIcon} label="Source">{source}</Row>
            <Row icon={DeviceMobile} label="Location">{loc}</Row>
          </div>

          <button
            onClick={() => navigate(`/handler-profile/${ctx.customerId}?from_room=${ctx.roomId || ''}`)}
            className="w-full mt-3 py-2 border border-indigo-300 text-indigo-700 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-indigo-50"
            data-testid="cdp-view-full-btn"
          >
            <ArrowSquareOut size={16} weight="bold" /> View Full Details
          </button>

          <div className="mt-4">
            <p className="text-xs font-bold text-slate-400 uppercase mb-2">Quick Actions</p>
            <div className="grid grid-cols-2 gap-2">
              <QA icon={NotePencil} label="Add Note" onClick={addNote} tone="border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100" />
              <QA icon={Prohibit} label="Block User" onClick={blockUser} tone="border-red-200 text-red-700 bg-red-50 hover:bg-red-100" />
            </div>
          </div>

          <div className="mt-4">
            <p className="text-xs font-bold text-slate-400 uppercase mb-2">Recent Activity</p>
            <div className="space-y-2">
              {totalRow && (
                <div className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-2" data-testid="cdp-total-row">
                  <span className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-600"><CurrencyInr size={14} weight="bold" /> {totalRow.label}</span>
                  <span className={`text-[13px] font-bold ${totalRow.tone}`}>{totalRow.value}</span>
                </div>
              )}
              {notes.map((nt) => (
                <div key={nt.id} className="flex gap-2 text-[12px]" data-testid="cdp-note-item"><span className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 flex-shrink-0" /><div><div className="text-slate-700">Note: {nt.note}</div><div className="text-slate-400">{nt.created_by_name} · {new Date(nt.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true })}</div></div></div>
              ))}
              {data?.created_at && (
                <div className="flex gap-2 text-[12px]"><span className="w-2 h-2 mt-1.5 rounded-full bg-emerald-500 flex-shrink-0" /><div><div className="text-slate-700">Account created</div><div className="text-slate-400">{created}</div></div></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailsPanel;
